





























import Vue from 'vue'
import mixins from 'vue-typed-mixins'
import { mapGetters, mapActions } from 'vuex'
import push from '@/push-notification'
import LoginContainer from '@/components/LoginContainer.vue'
import { insertCommaDelimiter, versionCompare, sleep } from '@/utils/helpers'

export default Vue.extend({
  name: 'Login',
  data() {
    return {
      account: null,
      userId: '',
      password: '',
      historyId: '',
      isLoggedIn: false,
      progress: false,
      isPossibleAddPoint: false,
      addPoint: 0,
      modal: null
    }
  },
  computed: {
    ...mapGetters('AppInfo', ['appVersion']),
    ...mapGetters('MyData', ['myName', 'myGenderLabel', 'myPoint']),

    hidden(): boolean {
      return this.$route.params.hidden === '1'
    },

    /**
     * ログイン用のパラメータとログイン成功時のコールバック関数
     */
    loginPayload(): any {
      return {
        user_id: this.userId,
        password: this.password,
        history_id: this.historyId,
        callback: this.loginCallback
      }
    },

    /**
     * ログイン情報がの有無
     */
    hasAccountInfo(): boolean {
      return this.userId.length > 0 && this.password.length > 0
    }
  },
  async created() {
    const paramsId = this.$route.params.id || (this.$route.query.id as string)
    const paramsPassword = this.$route.params.password || (this.$route.query.password as string)

    if (paramsId && paramsPassword) {
      // ログイン URL の処理を先に書かないと実行されなくなる
      // ここで historyId を更新すると複数アカウントを作成したときに古い方の historyId が上書きされてしまう
      this.userId = paramsId
      this.password = paramsPassword
    } else if (this.$_hasCordova) {
      // アンインストールなどで直接投句ページにアクセスしてきた際にアカウントを端末から復元
      try {
        this.account = await this.$_secureGetAccount()
        if (this.account.userId && this.account.password) {
          this.progress = true
          // id と pass が端末に保存されているならログイン済み
          this.isLoggedIn = true
          this.userId = this.account.userId
          this.password = this.account.password
          this.historyId = this.account.historyId
        }
      } catch (err) {
        console.error(err)
        return
      }
    }

    if (!this.isLoggedIn) {
      // ログイン済みじゃないならトークンの有効判定
      this.isLoggedIn = await this.$store.dispatch('Auth/verifyToken')
    }

    if (this.userId && this.password) {
      if (this.isLoggedIn) {
        // ログイン URL でアクセス
        const data = await this.$store.dispatch('Auth/fetchAuthInfo')
        if (
          this.hasAccountInfo &&
          (this.userId !== data.user_id || this.password !== data.password)
        ) {
          // ログイン済みで違うアカウントにログインしようとしているなら上書きするかチェック
          await this.$store.dispatch('MyData/getMyProfile')

          // 合算対象者かどうかをチェック
          await this.checkPossibleAddPoint()

          this.modal = this.$amatamodal.open({
            parent: this,
            props: {
              isPossibleAddPoint: this.isPossibleAddPoint,
              name: this.$store.getters['MyData/myName'],
              gender: this.$store.getters['MyData/myGenderLabel'],
              point: insertCommaDelimiter(this.$store.getters['MyData/myPoint']),
              addPoint: insertCommaDelimiter(this.addPoint + ''),
              totalPoint: insertCommaDelimiter(
                this.$store.getters['MyData/myPoint'] + this.addPoint
              ),
              onCancel: this.cancel,
              onLogin: this.overwriteLogin,
              onGassan: this.gassan
            },
            onCancel: this.closeModal,
            canCancel: ['x'],
            component: LoginContainer,
            scrollLock: true,
            scrollEnableSelector: '.modal-content .card'
          })
          return
        }
      }

      // ログイン URL でアクセスしてきた際にログインボタンを押さずに自動でログインさせる
      this.executeLogin()
    }
  },
  destroyed() {
    this.closeModal()
  },
  methods: {
    /**
     * ログインキャンセル
     */
    async cancel() {
      const buttonIndex = await this.$_confirm('キャンセルします。よろしいですか？', '')
      if (!this.$_clickedConfirmOK(buttonIndex)) {
        return
      }

      this.closeModal()

      // NOTE: PC 版 Chrome だと URL のパラメータよりも autocomplete の ID/PASSWORD が優先されるのが心配
      //       アプリ版・ Web 版では上書きログインできることを確認
      this.$router.replace('/')
    },

    /**
     * ログイン処理実行
     */
    async executeLogin() {
      if (this.userId.length === 0) {
        this.$_alert('ユーザーIDを入力してください')
        return
      }
      if (this.password.length === 0) {
        this.$_alert('パスワードを入力してください')
        return
      }

      this.closeModal()
      this.$_resetStoreState()

      if (this.$_platformIsAndroid) {
        const hasPermission = await this.$_checkAllowAccessToMediaFiles()
        if (!hasPermission) {
          this.$_alert(
            'もう一度[ログイン]をタップして、ストレージ(写真、メディア、ファイル)へのアクセスを許可してください。'
          )
          return
        }
      }

      if (this.userId && this.password) {
        this.progress = true
        try {
          const data = await this.$store.dispatch('Auth/login', this.loginPayload)
          if (data) {
            await this.errorCheck(data)
          }
          return
        } catch (err) {
          this.progress = false
          setTimeout(() => {
            this.$_alert(err)
            console.error(err)
          }, 0)
        }
      }
    },

    /**
     * 上書きログイン
     */
    async overwriteLogin() {
      let message = '新しいアカウント(B)で上書きログインします。よろしいですか？'
      if (this.isPossibleAddPoint) {
        message += '\n※ポイントは合算されません。'
      }
      const buttonIndex = await this.$_confirm(message, '')
      if (!this.$_clickedConfirmOK(buttonIndex)) {
        return
      }

      await this.executeLogin()
    },

    /**
     * ポイント合算対象者かチェック
     */
    async checkPossibleAddPoint() {
      try {
        const { canGassan, addPoint } = await this.$store.dispatch(
          'Auth/checkPointAddUp',
          this.userId
        )
        if (addPoint) {
          this.addPoint = addPoint
        }
        this.isPossibleAddPoint = canGassan
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * ポイント合算処理
     */
    async gassan() {
      const buttonIndex = await this.$_confirm('ポイント合算を実行します。よろしいですか？', '')
      if (!this.$_clickedConfirmOK(buttonIndex)) {
        return
      }
      if (!this.userId) {
        return
      }
      try {
        const { error } = await this.$store.dispatch('Auth/addUpPoint', this.userId)
        if (error) {
          this.$_alert('ポイント合算失敗')
          console.error(error)
          return
        }
        this.closeModal()
        this.$router.replace('/')
        this.$_alert('ポイントを合算しました')
      } catch (err) {
        console.error(err)
        this.$_alert(err)
      }
    },

    /**
     * ログイン処理のコールバック
     */
    async loginCallback(data: any) {
      // ユーザ情報を端末に保存
      if (this.$_hasCordova) {
        // 機種変更や登録ページをアプリでアクセスしない移行処理でも history_id が端末に保存されるようにする
        this.historyId = data.history_id
        await this.$_secureSaveAccount({
          userId: this.userId,
          password: this.password,
          historyId: this.historyId
        })

        // ここでは必ずプッシュ通知を求める
        push.initPushNotification(this.$root)
        // ios9 最新バージョン 9.3.5 より上なら iOS10 と判断
        if (this.$_platformIsIOS && versionCompare(device.version, '9.3.5')) {
          push.initVoIPPushNotification(this.$root)
        }
      }

      if (!this.$store.getters['MyData/initializedMyProfile']) {
        // 自分のデータをサーバから取得
        await this.$store.dispatch('MyData/getMyProfile')
      }

      this.progress = false
      location.href = '#/'
    },

    /**
     * パスワード再設定ページを開く
     */
    passwordForget() {
      const isCordova = this.$_hasCordova ? 1 : 0
      this.$_open(
        `${this.$store.getters.SERVER_URL}/api/get_sms.php?app=${isCordova}&page=pw_forget`
      )
    },

    /**
     * エラーチェック
     */
    async errorCheck(data: any) {
      if (!data.error) return

      if (typeof data.error === 'string') {
        this.$_alert(data.error)
        this.progress = false
        return
      }
      if (data.error.code === 2) {
        // アク禁
        this.$store.commit('Auth/setIsBan', true)
        this.$router.replace('/ban')
      } else if (data.error.code === 3) {
        // ユーザーが削除されていてアプリなら端末のデータを上書き削除
        if (this.$_hasCordova) {
          try {
            await this.$_secureSaveAccount({
              userId: '',
              password: ''
            })
          } catch (err) {
            console.error(err)
          }
        }
        this.$_alert(data.error.message)
        this.$router.replace('/singup')
      }
    },

    closeModal() {
      if (this.modal) {
        this.modal.close()
        this.modal = null
      }
    }
  }
})
